import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

import AdatisLogo from "../../../../Components/Common/AdatisLogo";
import withRouter from "../../../../Components/Common/withRouter";
import {
  digitsOnly,
  formatCountdown,
  isAuthRateLimited,
  resolveAuthClientError,
} from "../../../../helpers/authClientError";
import {
  getSuperAdminAuthSession,
  superAdminLogin,
  superAdminVerifyOtp,
} from "../../../../services/authService";
import ParticlesAuth from "../../../AuthenticationInner/ParticlesAuth";

const OTP_RESEND_COOLDOWN_SECONDS = 60;

const SuperAdminLogin = (props: any) => {
  const { t } = useTranslation();
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [emailHint, setEmailHint] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [showSpamHint, setShowSpamHint] = useState<boolean>(false);

  const credentialsSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().required(t("admin.login.validation.emailRequired")),
        password: Yup.string().required(
          t("admin.login.validation.passwordRequired")
        ),
      }),
    [t]
  );

  const otpSchema = useMemo(
    () =>
      Yup.object({
        code: Yup.string()
          .required(t("admin.login.validation.otpRequired"))
          .matches(/^\d{6}$/, t("admin.login.validation.otpFormat")),
      }),
    [t]
  );

  const presentError = (message: string) => {
    setErrorMessage(message);
    toast.error(message);
  };

  const otpForm = useFormik({
    enableReinitialize: true,
    initialValues: { code: "" },
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      if (!challengeId) {
        return;
      }

      setLoader(true);
      setErrorMessage("");

      try {
        const response = await superAdminVerifyOtp(challengeId, values.code);
        const session = getSuperAdminAuthSession();

        if (response.success && session) {
          if (response.message) {
            toast.success(response.message);
          }
          props.router.navigate("/super-admin/dashboard");
          return;
        }

        presentError(response.message || t("admin.login.toast.otpError"));
      } catch (error: unknown) {
        const resolved = resolveAuthClientError(
          error,
          t("admin.login.toast.otpError")
        );

        if (
          resolved.code === "OTP_EXPIRED" ||
          resolved.code === "OTP_LOCKED"
        ) {
          setResendCooldown(0);
          otpForm.setFieldValue("code", "");
          presentError(
            resolved.code === "OTP_EXPIRED"
              ? t("admin.login.toast.otpExpired")
              : t("admin.login.toast.otpLocked")
          );
          return;
        }

        if (resolved.code === "OTP_INVALID") {
          otpForm.setFieldValue("code", "");
          presentError(t("admin.login.toast.otpInvalid"));
          return;
        }

        presentError(
          isAuthRateLimited(resolved)
            ? t("admin.login.toast.rateLimited")
            : resolved.message
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const applyOtpChallenge = (payload: {
    challenge_id: string;
    email_hint?: string;
  }) => {
    setChallengeId(payload.challenge_id);
    setEmailHint(payload.email_hint || "");
    setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    setShowSpamHint(true);
    setErrorMessage("");
    otpForm.resetForm();
  };

  const requestOtpChallenge = async (
    email: string,
    password: string
  ): Promise<boolean> => {
    const response = await superAdminLogin(email, password);

    if (
      response.success &&
      response.data &&
      "requires_otp" in response.data &&
      response.data.requires_otp
    ) {
      applyOtpChallenge({
        challenge_id: response.data.challenge_id,
        email_hint: response.data.email_hint,
      });
      toast.success(response.message || t("admin.login.toast.otpSent"));
      return true;
    }

    const session = getSuperAdminAuthSession();
    if (response.success && session) {
      if (response.message) {
        toast.success(response.message);
      }
      props.router.navigate("/super-admin/dashboard");
      return true;
    }

    presentError(response.message || t("admin.login.toast.genericError"));
    return false;
  };

  const credentialsForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: credentialsSchema,
    onSubmit: async (values) => {
      setLoader(true);
      setErrorMessage("");

      try {
        await requestOtpChallenge(values.email, values.password);
      } catch (error: unknown) {
        const resolved = resolveAuthClientError(
          error,
          t("admin.login.toast.genericError")
        );
        presentError(
          isAuthRateLimited(resolved)
            ? t("admin.login.toast.rateLimited")
            : resolved.message
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const resetToCredentials = () => {
    setChallengeId(null);
    setEmailHint("");
    setResendCooldown(0);
    setShowSpamHint(false);
    setErrorMessage("");
    otpForm.resetForm();
  };

  const handleResendCode = async () => {
    if (
      resendCooldown > 0 ||
      loader ||
      !credentialsForm.values.email ||
      !credentialsForm.values.password
    ) {
      return;
    }

    setLoader(true);
    setErrorMessage("");

    try {
      await requestOtpChallenge(
        credentialsForm.values.email,
        credentialsForm.values.password
      );
    } catch (error: unknown) {
      const resolved = resolveAuthClientError(
        error,
        t("admin.login.toast.genericError")
      );
      presentError(
        isAuthRateLimited(resolved)
          ? t("admin.login.toast.rateLimited")
          : resolved.message
      );
    } finally {
      setLoader(false);
    }
  };

  const handleOtpInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    otpForm.setFieldValue("code", digitsOnly(event.target.value), true);
  };

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    otpForm.setFieldValue(
      "code",
      digitsOnly(event.clipboardData.getData("text")),
      true
    );
  };

  useEffect(() => {
    document.title = t("admin.login.pageTitle");
  }, [t]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <AdatisLogo variant="light" linkTo={false} />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">{t("admin.login.tagline")}</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">
                        {challengeId
                          ? t("admin.login.otp.title")
                          : t("admin.login.title")}
                      </h5>
                      <p className="text-muted">
                        {challengeId
                          ? t("admin.login.otp.subtitle", { email: emailHint })
                          : t("admin.login.subtitle")}
                      </p>
                    </div>
                    {errorMessage ? (
                      <Alert
                        color="danger"
                        className="mb-0"
                        role="alert"
                        aria-live="assertive"
                      >
                        {errorMessage}
                      </Alert>
                    ) : null}
                    {challengeId && showSpamHint ? (
                      <Alert
                        color="info"
                        className="mt-3 mb-0"
                        role="status"
                        aria-live="polite"
                      >
                        {t("admin.login.otp.spamHint")}
                      </Alert>
                    ) : null}
                    <div className="p-2 mt-4">
                      {challengeId ? (
                        <Form
                          onSubmit={(event) => {
                            event.preventDefault();
                            otpForm.handleSubmit();
                          }}
                        >
                          <div className="mb-3">
                            <Label
                              htmlFor="super-admin-otp"
                              className="form-label"
                            >
                              {t("admin.login.fields.otp.label")}
                            </Label>
                            <Input
                              id="super-admin-otp"
                              name="code"
                              className="form-control text-center fs-4"
                              placeholder={t(
                                "admin.login.fields.otp.placeholder"
                              )}
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              autoComplete="one-time-code"
                              autoFocus
                              maxLength={6}
                              onChange={handleOtpInputChange}
                              onPaste={handleOtpPaste}
                              onBlur={otpForm.handleBlur}
                              value={otpForm.values.code || ""}
                              invalid={
                                !!(otpForm.touched.code && otpForm.errors.code)
                              }
                            />
                            {otpForm.touched.code && otpForm.errors.code ? (
                              <FormFeedback type="invalid">
                                {otpForm.errors.code}
                              </FormFeedback>
                            ) : (
                              <div className="form-text">
                                {t("admin.login.otp.pasteHint")}
                              </div>
                            )}
                          </div>

                          <div className="mt-4">
                            <Button
                              color="success"
                              disabled={loader}
                              className="btn btn-success w-100"
                              type="submit"
                            >
                              {loader ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  {t("admin.login.actions.loading")}
                                </>
                              ) : (
                                t("admin.login.actions.verifyOtp")
                              )}
                            </Button>
                          </div>

                          <div className="mt-3 text-center">
                            <button
                              type="button"
                              className="btn btn-link"
                              disabled={loader || resendCooldown > 0}
                              onClick={handleResendCode}
                              aria-live="polite"
                            >
                              {resendCooldown > 0
                                ? t("admin.login.actions.resendIn", {
                                    time: formatCountdown(resendCooldown),
                                  })
                                : t("admin.login.actions.resendCode")}
                            </button>
                          </div>

                          <div className="mt-1 text-center">
                            <button
                              type="button"
                              className="btn btn-link text-muted"
                              onClick={resetToCredentials}
                            >
                              {t("admin.login.actions.changeEmail")}
                            </button>
                          </div>
                        </Form>
                      ) : (
                        <Form
                          onSubmit={(event) => {
                            event.preventDefault();
                            credentialsForm.handleSubmit();
                          }}
                        >
                          <div className="mb-3">
                            <Label
                              htmlFor="super-admin-email"
                              className="form-label"
                            >
                              {t("admin.login.fields.email.label")}
                            </Label>
                            <Input
                              id="super-admin-email"
                              name="email"
                              className="form-control"
                              placeholder={t(
                                "admin.login.fields.email.placeholder"
                              )}
                              type="email"
                              onChange={credentialsForm.handleChange}
                              onBlur={credentialsForm.handleBlur}
                              value={credentialsForm.values.email || ""}
                              invalid={
                                !!(
                                  credentialsForm.touched.email &&
                                  credentialsForm.errors.email
                                )
                              }
                            />
                            {credentialsForm.touched.email &&
                            credentialsForm.errors.email ? (
                              <FormFeedback type="invalid">
                                {credentialsForm.errors.email}
                              </FormFeedback>
                            ) : null}
                          </div>

                          <div className="mb-3">
                            <Label
                              className="form-label"
                              htmlFor="super-admin-password-input"
                            >
                              {t("admin.login.fields.password.label")}
                            </Label>
                            <div className="position-relative auth-pass-inputgroup mb-3">
                              <Input
                                id="super-admin-password-input"
                                name="password"
                                value={credentialsForm.values.password || ""}
                                type={passwordShow ? "text" : "password"}
                                className="form-control pe-5"
                                placeholder={t(
                                  "admin.login.fields.password.placeholder"
                                )}
                                onChange={credentialsForm.handleChange}
                                onBlur={credentialsForm.handleBlur}
                                invalid={
                                  !!(
                                    credentialsForm.touched.password &&
                                    credentialsForm.errors.password
                                  )
                                }
                              />
                              {credentialsForm.touched.password &&
                              credentialsForm.errors.password ? (
                                <FormFeedback type="invalid">
                                  {credentialsForm.errors.password}
                                </FormFeedback>
                              ) : null}
                              <button
                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                type="button"
                                id="super-admin-password-addon"
                                onClick={() => setPasswordShow(!passwordShow)}
                              >
                                <i className="ri-eye-fill align-middle"></i>
                              </button>
                            </div>
                          </div>

                          <div className="mt-4">
                            <Button
                              color="success"
                              disabled={loader}
                              className="btn btn-success w-100"
                              type="submit"
                            >
                              {loader ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  {t("admin.login.actions.loading")}
                                </>
                              ) : (
                                t("admin.login.actions.submit")
                              )}
                            </Button>
                          </div>
                        </Form>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(SuperAdminLogin);
